import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import { boundActivityDisplayText } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityDisplayText'
import { summarizeActivityItemNames } from 'tripPlanner/assistant/surfaceSnapshots/summarizeActivityItemNames'

function areValueSetsEqual(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false
  const bSet = new Set(b)
  return a.every(value => bSet.has(value))
}

// Describes what the resulting filter state means for this dimension -- undefined when there's
// truly nothing left to say (no values, no suffix). suffix carries its own parens (e.g.
// '(bookable only)') for the values-present case; stripped for the values-absent case so
// "bookable only" alone doesn't read as a stray parenthetical with nothing before it.
function describeResultingState(nextValues: ReadonlyArray<string>, suffix?: string): string | undefined {
  if (nextValues.length > 0) return suffix ? `${summarizeActivityItemNames(nextValues)} ${suffix}` : summarizeActivityItemNames(nextValues)
  return suffix?.replace(/^\(|\)$/g, '')
}

// Called once when the filter panel closes (or filters are cleared/auto-pruned), not once per
// checkbox toggle inside it -- a multi-select filter is one customer decision, not one per click,
// and dispatching per-click would flood the activity log with intermediate states the customer
// never actually settled on. previousValues/previousSuffix are used only for the no-op gate below
// and to tell "cleared" apart from "never set," never rendered directly.
//
// Returns undefined for a panel that closed with the same values (and suffix) it opened with -- a
// peek, not a change -- and for a dimension with nothing before and nothing now. Otherwise, an
// empty resulting set still gets a "cleared" entry rather than being silently dropped: this
// describes resulting state, not a delta, so staying silent on a real change would leave the
// model's most recent fact about this dimension stale (e.g. still believing the list is
// Tokyo-only after the customer clears the location filter).
export function buildListFilterChangedActivityEntry(
  dimension: 'date' | 'location' | 'type',
  previousValues: ReadonlyArray<string>,
  nextValues: ReadonlyArray<string>,
  suffix?: string,
  previousSuffix?: string,
): TripPlannerSurfaceActivityEntry | undefined {
  if (areValueSetsEqual(previousValues, nextValues) && previousSuffix === suffix) return undefined

  const resultingState = describeResultingState(nextValues, suffix)
  const displayText = resultingState ?
    `Filtered list by ${dimension}: ${resultingState}` :
    `Cleared ${dimension} filter`

  return {
    kind: 'list_filter_changed',
    label: 'Changed list filter',
    displayText: boundActivityDisplayText(displayText),
    sourcePath: 'trip_planner.list.filter',
    dataClassification: 'personalised_surface',
    occurredAt: new Date().toISOString(),
  }
}
